import { Trans } from 'react-i18next';

import {
    faArrowRightArrowLeft,
    faArrowRightFromBracket,
    faArrowRightToBracket,
    faCalendarCirclePlus,
    faClipboardList,
    faDiamondExclamation,
    faExclamationSquare,
} from '@fortawesome/pro-duotone-svg-icons';
import { t } from 'i18next';

function getOrderReference(notification = {}) {
    return notification.templateParams?.order?.shipperOrderReference || notification.templateParams?.order?.iid || '--';
}

function getOrderType(notification = {}) {
    return notification.templateParams?.order?.orderType ?? null;
}

function byOrderType(orderType, { EXIT, ENTRY, fallback }) {
    if (orderType === 'EXIT') return EXIT;
    if (orderType === 'ENTRY') return ENTRY;
    return fallback;
}

function getOrderIcon(notification = {}) {
    const orderType = getOrderType(notification);
    return byOrderType(orderType, {
        EXIT: faArrowRightFromBracket,
        ENTRY: faArrowRightToBracket,
        fallback: faArrowRightArrowLeft,
    });
}

function getOrderUrl(notification = {}, suffix = '') {
    const orderId = notification.templateParams?.order?.id;
    if (!orderId) {
        return '/exit-orders/';
    }

    const baseUrl =
        notification.templateParams?.order?.orderType === 'TRANSPORT'
            ? `/transports/${orderId}/`
            : notification.templateParams?.order?.orderType === 'EXIT'
              ? `/exit-orders/${orderId}/`
              : notification.templateParams?.order?.orderType === 'ENTRY'
                ? `/entry-orders/${orderId}/`
                : `/orders/${orderId}/`;
    return `${baseUrl}${suffix}`;
}

function getAdditionalRequestReference(notification = {}) {
    return notification.templateParams?.additionalRequest?.iid || '--';
}

function getAdditionalRequestUrl(notification = {}, suffix = '') {
    const additionalRequestId =
        notification.templateParams?.additionalRequest?.publicId || notification.templateParams?.additionalRequestId;

    if (!additionalRequestId) {
        return '/additional-requests/';
    }

    return `/additional-requests/${additionalRequestId}${suffix}`;
}

function getIncidentsUrl(notification = {}, suffix = '') {
    const incident = notification.templateParams?.incident;

    if (incident) {
        return `/logistic-management/incidents/?incidentId=${incident.id}${suffix}`;
    }

    return `/logistic-management/incidents/${suffix}`;
}

export const templates = {
    comment_added_to_order: {
        title() {
            return t('Comment');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New comment on exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New comment on entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New comment on order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            const commentId = notification.templateParams?.orderComment?.id;
            if (!commentId) {
                return getOrderUrl(notification, '#activities');
            }

            return getOrderUrl(notification, `?hlCommentId=comment-${commentId}#activities`);
        },
    },
    mentioned_in_order_comment: {
        title() {
            return t('Comment');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New comment on exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New comment on entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New comment on order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            const commentId = notification.templateParams?.orderComment?.id;
            if (!commentId) {
                return getOrderUrl(notification, '#activities');
            }

            return getOrderUrl(notification, `#activities?commentId=${commentId}`);
        },
    },
    comment_added_to_order_incident: {
        title() {
            return t('Comment');
        },
        icon() {
            return faDiamondExclamation;
        },
        content(notification = {}) {
            const incidentReference = notification.templateParams?.incident?.iid || '--';
            return (
                <Trans
                    i18nKey='New comment on incident <0>{{ incidentReference }}</0>.'
                    values={{ incidentReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getIncidentsUrl(notification);
        },
    },
    mentioned_in_incident_comment: {
        title() {
            return t('Comment');
        },
        icon() {
            return faDiamondExclamation;
        },
        content(notification = {}) {
            const incidentReference = notification.templateParams?.incident?.iid || '--';
            return (
                <Trans
                    i18nKey='New comment on incident <0>{{ incidentReference }}</0>.'
                    values={{ incidentReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getIncidentsUrl(notification);
        },
    },
    order_incident_created: {
        title() {
            return t('Incident');
        },
        icon() {
            return faDiamondExclamation;
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);

            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New incident on exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New incident on entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New incident on order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        groupContent(count) {
            return (
                <Trans
                    i18nKey='{{ count }} incidents created'
                    values={{ count }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getIncidentsUrl(notification);
        },
    },
    order_incident_closed: {
        title() {
            return t('Incident');
        },
        icon() {
            return faDiamondExclamation;
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Incident closed on exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Incident closed on entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Incident closed on order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getIncidentsUrl(notification);
        },
    },
    order_incident_canceled: {
        title() {
            return t('Incident');
        },
        icon() {
            return faDiamondExclamation;
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Incident canceled on exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Incident canceled on entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Incident canceled on order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getIncidentsUrl(notification);
        },
    },
    order_created_and_ready_to_be_executed_to_warehouse: {
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        groupContent(count) {
            return (
                <Trans
                    i18nKey='{{ count }} orders created'
                    values={{ count }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    order_created_by_warehouse_to_shipper: {
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        groupContent(count) {
            return (
                <Trans
                    i18nKey='{{ count }} orders created'
                    values={{ count }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    order_updated_by_shipper_to_warehouse: {
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Exit <0>{{ orderReference }}</0> updated.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Entry <0>{{ orderReference }}</0> updated.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Order <0>{{ orderReference }}</0> updated.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    order_updated_by_warehouse_to_shipper: {
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Exit <0>{{ orderReference }}</0> updated.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Entry <0>{{ orderReference }}</0> updated.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Order <0>{{ orderReference }}</0> updated.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    exit_order_blocked: {
        title() {
            return t('Issue');
        },
        icon() {
            return faExclamationSquare;
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            return (
                <Trans
                    i18nKey='New exit <0>{{ orderReference }}</0> blocked.'
                    values={{ orderReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        groupContent(count) {
            return (
                <Trans
                    i18nKey='{{ count }} exits blocked'
                    values={{ count }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    entry_order_blocked: {
        title() {
            return t('Issue');
        },
        icon() {
            return faExclamationSquare;
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            return (
                <Trans
                    i18nKey='Entry <0>{{ orderReference }}</0> blocked.'
                    values={{ orderReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        groupContent(count) {
            return (
                <Trans
                    i18nKey='{{ count }} entries blocked'
                    values={{ count }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    integration_issue: {
        title() {
            return t('Issue');
        },
        icon() {
            return faExclamationSquare;
        },
        content() {
            return t('New intergration error');
        },
        groupContent(count) {
            return (
                <Trans
                    i18nKey='{{ count }} integration errors'
                    values={{ count }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            const job = notification.templateParams?.job;
            if (job?.id) {
                return `/logistic-management/integrations/jobs/?selectedJob=${job?.id}`;
            }
            return '/logistic-management/integrations/jobs/';
        },
    },
    additional_request_created: {
        title() {
            return t('Additional request');
        },
        icon() {
            return faClipboardList;
        },
        content(notification = {}) {
            const additionalRequestReference = getAdditionalRequestReference(notification);
            return (
                <Trans
                    i18nKey='New additional request <0>{{ additionalRequestReference }}</0>.'
                    values={{ additionalRequestReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getAdditionalRequestUrl(notification);
        },
    },
    additional_request_edited: {
        title() {
            return t('Additional request');
        },
        icon() {
            return faClipboardList;
        },
        content(notification = {}) {
            const additionalRequestReference = getAdditionalRequestReference(notification);
            return (
                <Trans
                    i18nKey='Additional request <0>{{ additionalRequestReference }}</0> updated.'
                    values={{ additionalRequestReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getAdditionalRequestUrl(notification);
        },
    },
    additional_request_new_comment: {
        title() {
            return t('Additional request');
        },
        icon() {
            return faClipboardList;
        },
        content(notification = {}) {
            const additionalRequestReference = getAdditionalRequestReference(notification);
            return (
                <Trans
                    i18nKey='New comment on additional request <0>{{ additionalRequestReference }}</0>.'
                    values={{ additionalRequestReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getAdditionalRequestUrl(notification);
        },
    },
    mentioned_in_additional_request_comment: {
        title() {
            return t('Additional request');
        },
        icon() {
            return faClipboardList;
        },
        content(notification = {}) {
            const additionalRequestReference = getAdditionalRequestReference(notification);
            return (
                <Trans
                    i18nKey='New comment on additional request <0>{{ additionalRequestReference }}</0>.'
                    values={{ additionalRequestReference }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            return getAdditionalRequestUrl(notification);
        },
    },
    document_added_to_order: {
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New document on exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New document on entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New document on order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification, '#documents');
        },
    },
    order_canceled_by_shipper_to_warehouse: {
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Exit <0>{{ orderReference }}</0> canceled.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Entry <0>{{ orderReference }}</0> canceled.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Order <0>{{ orderReference }}</0> canceled.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification, '#activities');
        },
    },
    order_canceled_by_warehouse_to_shipper: {
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Exit <0>{{ orderReference }}</0> canceled.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Entry <0>{{ orderReference }}</0> canceled.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Order <0>{{ orderReference }}</0> canceled.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    order_completed_with_adjustment_to_warehouse: {
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Exit <0>{{ orderReference }}</0> completed with quantity adjustments.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Entry <0>{{ orderReference }}</0> completed with quantity adjustments.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Order <0>{{ orderReference }}</0> completed with quantity adjustments.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    order_completed_with_adjustment_to_shipper: {
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Exit <0>{{ orderReference }}</0> completed with quantity adjustments.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Entry <0>{{ orderReference }}</0> completed with quantity adjustments.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Order <0>{{ orderReference }}</0> completed with quantity adjustments.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    timeslot_confirmed_by_shipper_to_warehouse: {
        title() {
            return t('Scheduling');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Time slot confirmed for exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Time slot confirmed for entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Time slot confirmed for order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    timeslot_confirmed_by_warehouse_to_shipper: {
        title() {
            return t('Scheduling');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Time slot confirmed for exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Time slot confirmed for entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Time slot confirmed for order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    order_completed: {
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Exit <0>{{ orderReference }}</0> completed.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Entry <0>{{ orderReference }}</0> completed.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Order <0>{{ orderReference }}</0> completed.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    exit_order_marked_as_delivered: {
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Exit <0>{{ orderReference }}</0> delivered.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Entry <0>{{ orderReference }}</0> delivered.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Order <0>{{ orderReference }}</0> delivered.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    unconfirmed_order_creation: {
        title() {
            return t('Order');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New unconfirmed exit <0>{{ orderReference }}</0> to confirm.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New unconfirmed entry <0>{{ orderReference }}</0> to confirm.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New unconfirmed order <0>{{ orderReference }}</0> to confirm.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            if (notification.templateParams?.order?.id) {
                return `/logistic-management/unconfirmed-orders/${notification.templateParams?.order?.id}`;
            }
            return '/logistic-management/unconfirmed-orders';
        },
    },
    failure_auto_pdf_order_creation: {
        title() {
            return t('Issue');
        },
        icon() {
            return faExclamationSquare;
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='Failed to automatically generate the exit <0>{{ orderReference }}</0> from the PDF.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='Failed to automatically generate the entry <0>{{ orderReference }}</0> from the PDF.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='Failed to automatically generate the order <0>{{ orderReference }}</0> from the PDF.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            if (notification.templateParams?.order?.id) {
                return `/logistic-management/unconfirmed-orders/${notification.templateParams?.order?.id}`;
            }
            return '/logistic-management/unconfirmed-orders';
        },
    },
    need_to_confirm_planned_execution_datetime_to_shipper: {
        title() {
            return t('Scheduling');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New time slot requested for exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New time slot requested for entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New time slot requested for order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    need_to_confirm_planned_execution_datetime_to_warehouse: {
        title() {
            return t('Scheduling');
        },
        icon(notification = {}) {
            return getOrderIcon(notification);
        },
        content(notification = {}) {
            const orderReference = getOrderReference(notification);
            const orderType = getOrderType(notification);
            return byOrderType(orderType, {
                EXIT: (
                    <Trans
                        i18nKey='New time slot requested for exit <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                ENTRY: (
                    <Trans
                        i18nKey='New time slot requested for entry <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
                fallback: (
                    <Trans
                        i18nKey='New time slot requested for order <0>{{ orderReference }}</0>.'
                        values={{ orderReference }}
                        components={[
                            <span
                                className='font-semibold'
                                key='trans-span1'
                            />,
                        ]}
                    />
                ),
            });
        },
        url(notification = {}) {
            return getOrderUrl(notification);
        },
    },
    master_item_blocked_on_integration_sync_conditions: {
        title() {
            return t('Issue');
        },
        icon() {
            return faExclamationSquare;
        },
        content() {
            return t('New reference blocked');
        },
        groupContent(count) {
            return (
                <Trans
                    i18nKey='{{ count }} references blocked'
                    values={{ count }}
                    components={[
                        <span
                            className='font-semibold'
                            key='trans-span1'
                        />,
                    ]}
                />
            );
        },
        url(notification = {}) {
            const masterItem = notification.templateParams?.masterItem;
            if (masterItem?.id) {
                return `/logistic-management/inventory/items/${masterItem.id}`;
            }
            return '/logistic-management/inventory/items/';
        },
    },
    appointment_rescheduled_by_carrier: {
        title() {
            return t('Scheduling');
        },
        icon() {
            return faCalendarCirclePlus;
        },
        content() {
            return t('Appointment rescheduled.');
        },
        url(notification = {}) {
            const appointmentId = notification.templateParams?.appointment?.id;
            if (appointmentId) {
                return `/appointments/list/${appointmentId}`;
            }
            return '/appointments/list/';
        },
    },
    appointment_confirmation_for_lsp: {
        title() {
            return t('Scheduling');
        },
        icon() {
            return faCalendarCirclePlus;
        },
        content() {
            return t('Appointment confirmed.');
        },
        url(notification = {}) {
            const appointmentId = notification.templateParams?.appointment?.id;
            if (appointmentId) {
                return `/appointments/list/view/appointment/${appointmentId}`;
            }
            return '/appointments/list/';
        },
    },
};
